import {useIsOverviewEnabled} from '../../hooks/use-feature-state'
import {useState} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import type {HeaderPageData} from '../../page-data/payloads/header'
import {ReviewRequestBanner} from './ReviewRequestBanner'
import {CanStackBanner} from './CanStackBanner'
import {StackCreatedBanner, type StackCreatedInfo} from './StackCreatedBanner'
import type {PullRequestActiveRoute} from '../../hooks/use-active-route'
import {clsx} from 'clsx'
import headerStyles from '../PullRequestHeader.module.css'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {isBoolean} from '../../utils/validators'

interface PullRequestsTopBannersProps extends Pick<HeaderPageData, 'bannersData' | 'urls'> {
  activeRoute: PullRequestActiveRoute
  pullRequestId: number
  pullRequestNumber: number
  baseBranch: string
}

export function PullRequestsTopBanners({
  bannersData,
  urls,
  activeRoute,
  pullRequestId,
  pullRequestNumber,
  baseBranch,
}: PullRequestsTopBannersProps) {
  const showReviewRequestBanner = activeRoute === 'conversation' && bannersData.banners.reviewRequestBanner.render

  const [isCanStackBannerDismissed, setIsCanStackBannerDismissed] = useLocalStorage(
    `pr-can-stack-banner-dismissed:${pullRequestId}:${baseBranch}`,
    false,
    {validator: isBoolean},
  )
  const {isBrowser} = useRuntimeEnvironment()
  const [stackCreatedInfo, setStackCreatedInfo] = useState<StackCreatedInfo | null>(null)

  const shouldRenderCanStackBanner = bannersData.banners.canStackBanner.render
  const showCanStackBanner = shouldRenderCanStackBanner && !isCanStackBannerDismissed && isBrowser
  const shouldRenderStackCreatedBanner = stackCreatedInfo != null && !shouldRenderCanStackBanner

  const hasBanners = showReviewRequestBanner || showCanStackBanner || shouldRenderStackCreatedBanner

  const showUpdatedHeader = useIsOverviewEnabled()

  if (!hasBanners) {
    return null
  }

  return (
    <ErrorBoundary boundaryName="PullRequestsTopBanners" fallback={null}>
      <div
        className={clsx(
          'd-flex flex-column gap-2 tmp-mb-3',
          showUpdatedHeader && 'tmp-mt-3',
          showUpdatedHeader && headerStyles.constrainedHeader,
        )}
      >
        {showReviewRequestBanner && (
          <ReviewRequestBanner reviewRequest={bannersData.banners.reviewRequestBanner} urls={urls} />
        )}
        {showCanStackBanner && (
          <CanStackBanner
            pullRequestNumber={pullRequestNumber}
            onStackCreated={setStackCreatedInfo}
            onDismiss={() => setIsCanStackBannerDismissed(true)}
          />
        )}
        {/* If the PR is unstacked and the CanStackBanner now renders, hide the StackCreatedBanner */}
        {shouldRenderStackCreatedBanner && (
          <StackCreatedBanner info={stackCreatedInfo} onDismiss={() => setStackCreatedInfo(null)} />
        )}
      </div>
    </ErrorBoundary>
  )
}
